import React, { useState, useEffect, useRef } from 'react';
import './AnimatedFdbk.css';

const SmoothFdbkAnimation = () => {
  const [currentWord, setCurrentWord] = useState('fdbk');
  const [spans, setSpans] = useState([]);
  const [frameWidth, setFrameWidth] = useState('auto');
  const frameRef = useRef(null);
  const frameTextRef = useRef(null);
  const timeoutsRef = useRef([]);
  const animationsRef = useRef([]);
  
  const words = ['fdbk', 'feedback', 'fdbk'];
  
  // Get responsive values based on screen size
  const getResponsiveValues = () => {
    const width = window.innerWidth;
    
    if (width < 768) {
      return { thickness: 2, gap: 2, padding: 12 }; // Smaller padding
    } else if (width < 1024) {
      return { thickness: 2, gap: 2, padding: 15 }; // Compact padding
    } else {
      return { thickness: 3, gap: 3, padding: 18 }; // More compact than before
    }
  };
  
  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    timeoutsRef.current = [];
  };
  
  const clearAllAnimations = () => {
    animationsRef.current.forEach(animation => {
      if (animation && animation.pause) animation.pause();
    });
    animationsRef.current = [];
  };
  
  // Simple easing functions
  const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  const easeOutQuad = (t) => t * (2 - t);
  
  const animate = ({ targets, duration, easing, from, to, property, delay = 0, complete }) => {
    const startTime = Date.now() + delay;
    const easingFunc = easing === 'easeInOutQuad' ? easeInOutQuad : easeOutQuad;
    
    const step = () => {
      const now = Date.now();
      if (now < startTime) {
        requestAnimationFrame(step);
        return;
      }
      
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easingFunc(progress);
      
      if (Array.isArray(targets)) {
        targets.forEach(target => {
          if (property === 'opacity') {
            target.style.opacity = from + (to - from) * easedProgress;
          } else if (property === 'translateY') {
            target.style.transform = `translateY(${from + (to - from) * easedProgress}em)`;
          } else if (property === 'width') {
            target.style.width = `${from + (to - from) * easedProgress}px`;
          }
        });
      } else {
        if (property === 'width') {
          targets.style.width = `${from + (to - from) * easedProgress}px`;
        }
      }
      
      if (progress < 1) {
        requestAnimationFrame(step);
      } else if (complete) {
        complete();
      }
    };
    
    const animation = { pause: () => {} };
    animationsRef.current.push(animation);
    requestAnimationFrame(step);
    return animation;
  };
  
  const setWord = (word, initial = false) => {
    const newSpans = word.split('').map((char, index) => ({
      char,
      key: `${word}-${index}`,
      opacity: initial ? 1 : 0,
      translateY: initial ? 0 : 0.5
    }));
    setSpans(newSpans);
    setCurrentWord(word);
  };
  
  const measureWidth = () => {
    if (!frameTextRef.current) return 0;
    
    const textWidth = frameTextRef.current.scrollWidth;
    const { thickness, gap, padding } = getResponsiveValues();
    
    // For left-aligned text, we need left padding + text width + some right padding
    const rightPadding = padding * 0.5; // Less padding on the right since text is left-aligned
    return padding + textWidth + rightPadding + gap + thickness;
  };
  
  const animateTo = (word, callback) => {
    setWord(word, false);
    
    requestAnimationFrame(() => {
      const targetWidth = measureWidth();
      
      // Animate frame width
      if (frameRef.current) {
        const currentWidth = frameRef.current.offsetWidth;
        animate({
          targets: frameRef.current,
          duration: 600,
          easing: 'easeInOutQuad',
          from: currentWidth,
          to: targetWidth,
          property: 'width'
        });
      }
      
      // Animate text spans
      const spanElements = frameTextRef.current?.querySelectorAll('.text-span') || [];
      spanElements.forEach((span, index) => {
        const randomDelay = Math.random() * 500;
        
        // Opacity animation
        animate({
          targets: [span],
          duration: 600,
          easing: 'easeOutQuad',
          from: 0,
          to: 1,
          property: 'opacity',
          delay: randomDelay
        });
        
        // TranslateY animation
        animate({
          targets: [span],
          duration: 600,
          easing: 'easeOutQuad',
          from: 0.5,
          to: 0,
          property: 'translateY',
          delay: randomDelay,
          complete: index === spanElements.length - 1 ? callback : null
        });
      });
    });
  };
  
  const runAnimationSequence = () => {
    clearAllTimeouts();
    clearAllAnimations();
    
    animateTo(words[1], () => {
      const timeout = setTimeout(() => {
        animateTo(words[2], null);
      }, 2000);
      timeoutsRef.current.push(timeout);
    });
  };
  
  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (frameTextRef.current) {
        const newWidth = measureWidth();
        setFrameWidth(`${newWidth}px`);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentWord]);
  
  // Initialize
  useEffect(() => {
    // Initialize with first word
    setWord(words[0], true);
    
    requestAnimationFrame(() => {
      if (frameTextRef.current) {
        const initWidth = measureWidth();
        setFrameWidth(`${initWidth}px`);
      }
    });
    
    // Start animation sequence
    const timeout = setTimeout(runAnimationSequence, 1500);
    timeoutsRef.current.push(timeout);
    
    return () => {
      clearAllTimeouts();
      clearAllAnimations();
    };
  }, []);

  return (
    <div 
      ref={frameRef}
      onClick={runAnimationSequence}
      className="fdbk-animation-container"
      style={{ width: frameWidth }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          runAnimationSequence();
        }
      }}
      aria-label="Click to animate feedback text"
    >
      {/* Frame borders */}
      <div className="frame-border frame-border--top" />
      <div className="frame-border frame-border--left" />
      <div className="frame-border frame-border--bottom" />
      <div className="frame-border frame-border--right-top" />
      <div className="frame-border frame-border--right-bottom" />
      
      {/* Text */}
      <div ref={frameTextRef} className="text-container">
        {spans.map((spanData) => (
          <span
            key={spanData.key}
            className="text-span"
            style={{
              opacity: spanData.opacity,
              transform: `translateY(${spanData.translateY}em)`
            }}
          >
            {spanData.char}
          </span>
        ))}
      </div>
    </div>
  );
};

export default SmoothFdbkAnimation;